"use client";

import { Menu as MenuPrimitive } from "@base-ui/react/menu";

import { cx } from "../../cx";
import styles from "./dropdown-menu.module.css";

type WithClassName<P> = Omit<P, "className"> & { className?: string };

function DropdownMenu(props: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root {...props} />;
}

function DropdownMenuPortal(props: MenuPrimitive.Portal.Props) {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}

function DropdownMenuTrigger({
  className,
  ...props
}: WithClassName<MenuPrimitive.Trigger.Props>) {
  return (
    <MenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      className={cx(styles.trigger, className)}
      {...props}
    />
  );
}

type DropdownMenuContentProps = WithClassName<MenuPrimitive.Popup.Props> &
  Pick<
    MenuPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  > & {
    container?: HTMLElement | null;
  };

function DropdownMenuContent({
  className,
  sideOffset = 4,
  side = "bottom",
  align = "center",
  alignOffset = 0,
  container,
  ...props
}: DropdownMenuContentProps) {
  return (
    <DropdownMenuPortal container={container ?? undefined}>
      <MenuPrimitive.Positioner
        data-slot="dropdown-menu-positioner"
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className={styles.positioner}
      >
        <MenuPrimitive.Popup
          data-tui=""
          data-slot="dropdown-menu-content"
          className={cx(styles.popup, className)}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </DropdownMenuPortal>
  );
}

type DropdownMenuItemProps = WithClassName<MenuPrimitive.Item.Props> & {
  inset?: boolean;
  variant?: "default" | "destructive";
  /** Radix-era alias of `onClick`; both fire when both are passed. */
  onSelect?: (event: Event) => void;
};

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  onSelect,
  onClick,
  ...props
}: DropdownMenuItemProps) {
  const mergedClick: MenuPrimitive.Item.Props["onClick"] =
    onSelect || onClick
      ? (event) => {
          onClick?.(event);
          onSelect?.(event.nativeEvent);
        }
      : undefined;
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset || undefined}
      data-variant={variant}
      className={cx(styles.item, className)}
      onClick={mergedClick}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: WithClassName<MenuPrimitive.Separator.Props>) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cx(styles.separator, className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
